import React, { Fragment, useState } from 'react';
import {
    AppBar,
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Container,
    Divider,
    Grid,
    Paper,
    Stack,
    Tab,
    Tabs,
    Toolbar,
    Typography,
    Pagination,
    IconButton,
    Select,
    MenuItem,
    Link,
} from '@mui/material';
import { CardSummary } from '../../../components/Common/CardSummary';
import { useGetDashboardCountQuery } from '../../../store/common/commonApis';
import DashboardSkeleton from '../../../components/Common/DashboardSkeleton';
import { isAuth } from '../../../helpers/cookies';
import { ProfileForm } from '../../../components/admin/profile/ProfileForm';
import { ChangePasswordForm } from '../../../components/admin/profile/ChangePasswordForm';
import { useGetAuthUserQuery } from '../../../store/user/userApis';
import { useGetSansthaListQuery } from '../../../store/sanstha/sansthaApis';
import { useGetMembersListQuery } from '../../../store/members/membersApis';
import { adminUrl, baseUrl, websiteUrl } from '../../../helpers/constants';
import { useTheme } from '@emotion/react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';

const DashboardContainer = () => {
    const theme = useTheme();
    const authData = isAuth();
    const userRoles = authData.roles;

    const [sansthaId, setSansthaId] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const limit = 12;

    const { data, isLoading } = useGetDashboardCountQuery();
    const { data: userData } = useGetAuthUserQuery();

    const referralLink = `${websiteUrl}/registration?ref=${userData?.user_id}`;

    const [language, setLanguage] = useState('en'); // Default to English

    const messages = {
        en: `Join our community! Register now using the link below:\n${referralLink}\nGet exclusive benefits and be part of something great!`,
        hi: `हमारे समुदाय से जुड़ें! नीचे दिए गए लिंक से अभी रजिस्टर करें:\n${referralLink}\nविशेष लाभ प्राप्त करें और कुछ महान का हिस्सा बनें!`,
    };

    const { data: sansthaList } = useGetSansthaListQuery({
        limit: 100,
        from_portal: 'false',
    });
    const { data: memberList, isFetching } = useGetMembersListQuery({
        pageNo: currentPage,
        limit,
        member_status: 'Member',
        sanstha_id: sansthaId || '',
        from_portal: 'false',
    });

    const [currentTab, setCurrentTab] = useState(0);

    const handleChange = (event, newValue) => setCurrentTab(newValue);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(messages[language]);
        alert('Referral link copied to clipboard!');
    };

    const shareReferral = () => {
        if (navigator.share) {
            navigator
                .share({
                    title: 'Register Now!',
                    text: messages[language],
                    url: referralLink,
                })
                .catch((err) => console.log('Error sharing:', err));
        } else {
            alert('Sharing is not supported on this device.');
        }
    };

    return (
        <>
            <AppBar position="static">
                <Toolbar>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        My Dashboard
                    </Typography>
                    <Button>
                        {' '}
                        <Link
                            href={adminUrl + '/privacy-policy'}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ textDecoration: 'none', color: '#fff' }}
                        >
                            Privacy Policy
                        </Link>
                    </Button>
                </Toolbar>
            </AppBar>

            {isLoading ? (
                <DashboardSkeleton />
            ) : (
                <Container maxWidth={false} sx={{ mt: 4, width: '100%', maxWidth: '100vw', paddingX: 0 }}>
                    <Stack spacing={4}>
                        {/* Project Introduction Section */}
                        <Paper elevation={3} sx={{ padding: 3, textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <img src={`assets/images/logo.png`} alt="Agarwal Projects" width={100} />
                            </Box>
                            <Typography variant="h4" fontWeight="bold" gutterBottom>
                                Welcome to Agarwal Projects
                            </Typography>
                            <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                                Share your profile and help build a strong community. By referring new members, you get
                                exclusive benefits in the future. Your referred members will be tracked under your
                                account.
                            </Typography>
                            <Typography variant="h6" fontWeight="bold">
                                Share Your Referral Link:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                                <Typography variant="body1" sx={{ mr: 1, wordBreak: 'break-all' }}>
                                    {referralLink}
                                </Typography>
                                <IconButton onClick={copyToClipboard} color="primary">
                                    <ContentCopyIcon />
                                </IconButton>
                            </Box>

                            {/* Language Selector for Share Message */}
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <Typography variant="body1" sx={{ mr: 1 }}>
                                    Select Language:+
                                </Typography>
                                <Select value={language} onChange={(e) => setLanguage(e.target.value)} size="small">
                                    <MenuItem value="en">English</MenuItem>
                                    <MenuItem value="hi">हिंदी</MenuItem>
                                </Select>
                            </Box>

                            {/* Share Button */}
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<ShareIcon />}
                                sx={{ mt: 2 }}
                                onClick={shareReferral}
                            >
                                Share Link
                            </Button>
                        </Paper>

                        <Grid container spacing={1} sx={{ width: '100% !important' }}>
                            {userRoles === 'admin' && (
                                <Grid item xl={4} lg={4} md={4} sm={6} xs={12} sx={{ paddingLeft: '0px !important' }}>
                                    <CardSummary title="Total Number of Sanstha" value={data.sanstha} />
                                </Grid>
                            )}
                            <Grid
                                item
                                xl={4}
                                lg={4}
                                md={4}
                                sm={6}
                                xs={12}
                                sx={{ paddingLeft: userRoles === 'admin' ? '0px' : '0px !important' }}
                            >
                                <CardSummary
                                    title={
                                        userRoles === 'admin'
                                            ? 'Total Number of Members'
                                            : 'Total Number of Members Added by You'
                                    }
                                    value={data.member}
                                />
                            </Grid>
                            <Grid item xl={4} lg={4} md={4} sm={6} xs={12}>
                                <CardSummary
                                    title={
                                        userRoles === 'admin'
                                            ? 'Total Number of Candidates'
                                            : 'Total Number of Candidates Added by You'
                                    }
                                    value={data.candidate}
                                />
                            </Grid>
                        </Grid>

                        <Paper elevation={3}>
                            <Tabs value={currentTab} onChange={handleChange} variant="scrollable" scrollButtons="auto">
                                <Tab value={0} label="Profile" />
                                <Tab value={1} label="Change Password" />
                                <Tab value={2} label="Sanstha" />
                                <Tab value={3} label="Members" />
                            </Tabs>
                            {currentTab === 0 && (
                                <Box sx={{ p: 3 }}>
                                    <ProfileForm initialValue={userData} roles={authData?.roles} />
                                </Box>
                            )}
                            {currentTab === 1 && (
                                <Box sx={{ p: 3 }}>
                                    <ChangePasswordForm
                                        roles={authData?.roles}
                                        user_id={userData?.user_id}
                                        data={userData}
                                    />
                                </Box>
                            )}
                            {currentTab === 2 && (
                                <Paper elevation={3} sx={{ padding: 3 }}>
                                    <Typography variant="h5" gutterBottom position="relative">
                                        {'List of Sanstha'}
                                        {sansthaId && (
                                            <Button
                                                sx={{ position: 'absolute', right: 0 }}
                                                onClick={() => setSansthaId(null)}
                                            >
                                                Clear
                                            </Button>
                                        )}
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Grid container spacing={2}>
                                        {sansthaList &&
                                            sansthaList?.result.length > 0 &&
                                            sansthaList?.result?.map((sanstha) => (
                                                <Grid item xs={12} md={4} key={sanstha._id}>
                                                    <Card
                                                        onClick={() => setSansthaId(sanstha._id)}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            height: '100%',
                                                            padding: 2,
                                                            backgroundColor:
                                                                sansthaId === sanstha._id ? '#e0e0e0' : '#fff',
                                                        }}
                                                        elevation={3}
                                                    >
                                                        <img
                                                            width={100}
                                                            src={baseUrl + '/' + sanstha.logo}
                                                            alt={sanstha.name}
                                                        />
                                                        <CardContent>
                                                            <Typography variant="h6">{sanstha.name}</Typography>
                                                            <Typography variant="body2" color="textSecondary">
                                                                {sanstha.description}
                                                            </Typography>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                    </Grid>
                                </Paper>
                            )}
                        </Paper>

                        <Paper elevation={3} sx={{ padding: 3 }}>
                            <Typography variant="h5" gutterBottom>
                                All Members
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            {memberList && memberList?.result?.length > 0 ? (
                                <Fragment>
                                    <Grid container spacing={2}>
                                        {isFetching ? (
                                            <DashboardSkeleton />
                                        ) : (
                                            memberList?.result?.map((member) => (
                                                <Grid item xs={12} md={4} key={member.id}>
                                                    <Card sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                                                        <CardMedia
                                                            component="img"
                                                            sx={{
                                                                width: 80,
                                                                height: 80,
                                                                borderRadius: '50%', // Make image round
                                                                objectFit: 'cover',
                                                                mr: 2,
                                                            }}
                                                            image={baseUrl + '/' + member.profile}
                                                            alt={member.name}
                                                        />
                                                        <CardContent>
                                                            <Typography variant="h6">{member.name}</Typography>
                                                            <Typography color="textSecondary">
                                                                {member.gender}
                                                            </Typography>
                                                            <Typography sx={{ fontWeight: 'bold' }}>
                                                                ID: {member.uuid}
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                {member.bio || 'No bio available'}
                                                            </Typography>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))
                                        )}
                                    </Grid>

                                    <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
                                        <Pagination
                                            count={Math.ceil(memberList?.count / limit)}
                                            page={currentPage}
                                            onChange={handlePageChange}
                                            color="primary"
                                        />
                                    </Stack>
                                </Fragment>
                            ) : (
                                <Typography variant="body1">No members found.</Typography>
                            )}
                        </Paper>
                    </Stack>
                </Container>
            )}
        </>
    );
};

export default DashboardContainer;
